import { AfterContentInit, AfterViewInit, Directive, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { GasFormKeyDirective } from '../../utils/gas-form-key/gas-form-key.directive';
import { GasForm } from '../../forms/gas-form.namespace';
import { GasFiltersComponent } from './gas-filters.component';
import { GasTable } from '../gas-table/gas-table.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import AdvancedFilterValue = GasTable.AdvancedFilterValue;


/** @description в директиву вынесена часть логики работы компонента GasFiltersComponent, навешивается автоматически */
@UntilDestroy()
@Directive({
  selector: '[gasFiltersComponent], gas-filters',
})
export class GasFiltersDirective implements AfterContentInit, AfterViewInit {

  @Output('filterChanged')
  public filterChanged$ = new EventEmitter<AdvancedFilterValue>();

  public filterGroup: FormGroup;

  public modifiedKeys: string[] = [];
  public modifiedInputs: GasFormKeyDirective[] = [];

  // Фильтр со всеми пустыми полями, для корректного обнуления
  private emptyFilter: AdvancedFilterValue = {};
  // Фильтр с дефолтными значениями (с которыми добавляли контрол), для возврата к InputState.Default
  private defaultFilter: AdvancedFilterValue = {};
  // Фильтр, содержащий последние примененные изменения, для кнопки undo
  private lastAppliedFilter: AdvancedFilterValue = {};

  public set state(type: GasForm.InputState) {
    this.updateFilterState(type);
  }

  public get state(): GasForm.InputState {
    return this.gasFiltersComponent.state;
  }

  constructor(
    public gasFiltersComponent: GasFiltersComponent,
    private formBuilder: FormBuilder,
  ) {
    this.filterGroup = this.formBuilder.group([]);
  }

  public ngAfterContentInit(): void {
    // Инициализация подписок нажатия кнопок "Сбросить все фильтры", "Отменить текущие изменения" и "Применить"
    // Внутри подписок обновляется визуальное состояние вложенных полей ввода и компонента обёртки
    this.initClearAllBtnClickSubscription();
    this.initClearCurrentSubscription();
    this.initUndoCurrentChangesBtnClickSubscription();
    this.initApplyBtnClickSubscription();
  }

  public ngAfterViewInit(): void {
    // При проставлении значений по умолчанию срабатывают valueChanges и фильтр становится InputState.Primary,
    // Передаём состояние Success, переопределится на Default в случае идентичности фильтров
    this.calculateInputState(GasForm.InputState.Success);
  }

  public addControl(key: string, control: FormControl): void {
    this.filterGroup.addControl(key, control);
    // При добавлении контрола, закинем его пустое значение в emptyFilter
    this.emptyFilter[key] = Array.isArray(control.value) ? [] : null;
    // А в дефолтный фильтр закинем дефолтное значение, для получения default состояния
    this.defaultFilter[key] = Array.isArray(control.value) ? Object.assign([], control.value) : control.value;
  }

  public deleteControl(controlKey: string): void {
    this.filterGroup.removeControl(controlKey);

    this.modifiedInputs = this.modifiedInputs
      .filter(component => component.gasFormKey !== controlKey);

    this.modifiedKeys = this.modifiedKeys
      .filter(key => key !== controlKey);

    delete this.emptyFilter[controlKey];
    delete this.defaultFilter[controlKey];
    delete this.lastAppliedFilter[controlKey];

    // Возможно удалили последний измененный контрол, отличающий состояние формы от default
    this.calculateInputState(GasForm.InputState.Success);
  }

  /** Очищаем все фильтры */
  private initClearAllBtnClickSubscription(): void {
    this.gasFiltersComponent.clearedAllFilters$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => {
        // Очищаем все фильтры
        this.emitFilterModel(JSON.parse(JSON.stringify(this.emptyFilter)));
        // Если использовали resetCallback, чтобы проставить дефолтные значения, надо это проверить
        setTimeout(() => this.calculateInputState(GasForm.InputState.Default), 0);
      });
  }

  /** Очищаем конкретный фильтр */
  private initClearCurrentSubscription(): void {
    this.gasFiltersComponent.clearCurrentFilters$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((directives: GasFormKeyDirective | GasFormKeyDirective[]) => {
        const changeDirectives: GasFormKeyDirective[] = Array.isArray(directives) ? directives : [directives];

        changeDirectives.forEach((directive: GasFormKeyDirective) => {
          const defaultValue = this.emptyFilter[directive.gasFormKey];
          const isArray = Array.isArray(defaultValue);

          this.filterGroup.patchValue({ [directive.gasFormKey]: isArray ? [] : null });

          this.modifiedKeys = this.modifiedKeys.filter(key => key !== directive.gasFormKey);
          this.modifiedInputs = this.modifiedInputs.filter(input => input.gasFormKey !== directive.gasFormKey);

          directive.component.state = GasForm.InputState.Default;
        });
        this.emitFilterModel(this.filterGroup.getRawValue());
      });
  }

  private initUndoCurrentChangesBtnClickSubscription(): void {
    this.gasFiltersComponent.undoCurrentChanged$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => {
        // Если был последний примененный фильтр, установим его
        // Иначе ставим дефолтные значения
        const filter = Object.keys(this.lastAppliedFilter).length > 0
          ? this.lastAppliedFilter
          : this.defaultFilter;
        this.emitFilterModel(filter);
      });
  }

  private initApplyBtnClickSubscription(): void {
    this.gasFiltersComponent.applied$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(() => {
        // filterGroup.getRawValue нужно, чтобы мы получили значение всех полей,
        // Если взять просто filterGroup.value, мы не получим задизебленные поля
        this.emitFilterModel(this.filterGroup.getRawValue(), false);
      });
  }

  private emitFilterModel(filterModel: AdvancedFilterValue, emitForm: boolean = true): void {
    this.filterGroup.setValue(filterModel, { emitEvent: emitForm });
    this.filterChanged$.emit(JSON.parse(JSON.stringify(this.filterGroup.getRawValue())));
    // Передаём состояние Success, переопределится на Default в случае идентичности фильтров
    this.calculateInputState(GasForm.InputState.Success);
  }

  /** Расчет состояния фильтра, установка Default в случае идентичности текущих фильтров и defaultFilter */
  private calculateInputState(inputState: GasForm.InputState): void {
    // Смотрим на getRawValue, чтобы поймать значения фильтров на текущий момент
    const currentFormFilter = JSON.stringify(this.filterGroup.getRawValue());

    // Сохраняем как последний примененный фильтр
    // Конвертация через JSON.parse обеспечивает deepCopy для вложенных массивов и объектов
    this.lastAppliedFilter = JSON.parse(currentFormFilter);

    // Если мы совпадаем с дефолтным фильтром, установим InputState.Default
    if (currentFormFilter === JSON.stringify(this.defaultFilter)) {
      this.updateFilterState(GasForm.InputState.Default);
    } else {
      // Принудительный сброс в default у нас только в одном месте - полное очищение фильтров
      // Сделано для поддержки переоткрытия компонентов с сохраненным фильтров, отличным от дефолтного
      // поэтому в этом случае надо перезаписать defaultFilter
      if (inputState === GasForm.InputState.Default) {
        this.defaultFilter = JSON.parse(currentFormFilter);
      }
      this.updateFilterState(inputState);
    }
  }

  /** Установка состояния фильтра */
  private updateFilterState(inputState: GasForm.InputState = GasForm.InputState.Default): void {
    this.gasFiltersComponent.state = inputState;
  }
}
